import {
  CLASIFICACION_HOMOLOGACION,
  PREFIJO_DSCLASIFICACION_CAPACITACION,
  TIPO_CITA
} from './constants-ejb';

/**
 * Datos mínimos necesarios para determinar el tipo de cita (Campo/Informe).
 * Distintas pantallas alimentan uno u otro campo según el origen de los datos
 * (dstipoCita como texto descriptivo, nmconsecutivoTipoCita o dstipo como código).
 */
export interface TipoCitaInfo {
  dstipoCita?: string | null;
  nmconsecutivoTipoCita?: number | string | null;
  dstipo?: string | number | null;
}

function resolverCodigoTipoCita(info: TipoCitaInfo): number {
  return Number(info.nmconsecutivoTipoCita ?? info.dstipo ?? 0);
}

/**
 * Determina si el tipo de cita corresponde a "Campo" (incluye Campo Complementario).
 * Verifica tanto el texto descriptivo (dstipoCita) como el código numérico, ya que
 * distintas pantallas del legacy alimentan uno u otro según el origen de los datos.
 *
 * Legacy: CreateOrUpdateCumplimientoViewImpl.isTipoCampo()
 */
export function esTipoCitaCampo(info: TipoCitaInfo | null | undefined): boolean {
  if (!info) return false;
  const dsTipoCita = (info.dstipoCita || '').toUpperCase();
  const matchTexto = ['CAMPO', 'CAMPO COMPLEMENTARIO'].includes(dsTipoCita);
  const codigo = resolverCodigoTipoCita(info);
  const matchCodigo = codigo === TIPO_CITA.CAMPO || codigo === TIPO_CITA.CAMPO_COMPLEMENTARIO;
  return matchTexto || matchCodigo;
}

/**
 * Determina si el tipo de cita corresponde a "Informe" (incluye Informe Complementario).
 *
 * Legacy: CreateOrUpdateCumplimientoViewImpl (rama contraria a isTipoCampo())
 */
export function esTipoCitaInforme(info: TipoCitaInfo | null | undefined): boolean {
  if (!info) return false;
  const dsTipoCita = (info.dstipoCita || '').toUpperCase();
  const matchTexto = ['INFORME', 'INFORME COMPLEMENTARIO'].includes(dsTipoCita);
  const codigo = resolverCodigoTipoCita(info);
  const matchCodigo = codigo === TIPO_CITA.INFORME || codigo === TIPO_CITA.INFORME_COMPLEMENTARIO;
  return matchTexto || matchCodigo;
}

/**
 * Determina si la clasificación del servicio homologado (lo que el negocio llama
 * "tipo de orden de servicio") corresponde a Capacitación.
 *
 * El código (`cdclasificacion`) es la fuente autoritativa; la descripción
 * (`dsclasificacion`) se usa como señal secundaria para las pantallas que solo
 * disponen del texto.
 *
 * Legacy: `CreateOrUpdateCumplimientoViewImpl.setTpscOrdenservicioDetalDto()`
 * (`"3".equals(cap)`) e `isActaCapacitacion()` (`dsclasificacion.startsWith("CAPACITACI")`).
 */
export function esClasificacionCapacitacion(
  cdclasificacion?: string | number | null,
  dsclasificacion?: string | null
): boolean {
  const codigo = String(cdclasificacion ?? '').trim();
  if (codigo) {
    return codigo === CLASIFICACION_HOMOLOGACION.CAPACITACION;
  }
  const descripcion = (dsclasificacion || '').trim().toUpperCase();
  return descripcion.startsWith(PREFIJO_DSCLASIFICACION_CAPACITACION);
}

/**
 * Filtra las opciones del combo "Tipo de cita" según el tipo de orden de servicio.
 *
 * - Capacitación → únicamente "Campo" (código 17).
 * - Cualquier otra clasificación (p. ej. Asesoría) → se devuelven todas las
 *   opciones del catálogo (Campo e Informe).
 *
 * Legacy: `clasificacionCapacitacion(boolean)` inyecta `NMCONSECUTIVO = 17` como
 * filtro del list box cuando la clasificación es Capacitación.
 */
export function filtrarTiposCitaPorClasificacion<T extends { value: number }>(
  opciones: T[],
  cdclasificacion?: string | number | null,
  dsclasificacion?: string | null
): T[] {
  if (!esClasificacionCapacitacion(cdclasificacion, dsclasificacion)) {
    return opciones;
  }
  return opciones.filter((op) => Number(op.value) === TIPO_CITA.CAMPO);
}

/**
 * Indica si un tipo de cita está permitido para la clasificación de la orden.
 * Espejo en cliente de la validación server-side del registro de cumplimiento.
 */
export function tipoCitaPermitidoParaClasificacion(
  cdtipoCita: number | string | null | undefined,
  cdclasificacion?: string | number | null,
  dsclasificacion?: string | null
): boolean {
  if (!esClasificacionCapacitacion(cdclasificacion, dsclasificacion)) {
    return true;
  }
  return Number(cdtipoCita ?? 0) === TIPO_CITA.CAMPO;
}
